import { useRef } from 'react';
import { cn } from 'lib/cn';
import { Button } from 'components/ui';
import { ImageIcon } from 'components/icons';

/**
 * Image field that reads a chosen file to a base64 data URI and reports it via
 * `onChange`. `value` may be an existing image URL or a data URI; both render in
 * the preview. Pass a string and get a string back (URL/data-URI or '').
 */
export function ImageUploadField({ label, value, onChange, hint, className }) {
  const inputRef = useRef(null);

  const handleFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onChange(typeof reader.result === 'string' ? reader.result : '');
    reader.readAsDataURL(file);
    event.target.value = ''; // allow re-picking the same file
  };

  return (
    <div className={className}>
      {label && <span className="mb-1.5 block text-sm font-medium text-slate-700">{label}</span>}
      <div className="flex items-center gap-3">
        <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-surface-200 bg-surface-100">
          {value ? (
            <img
              src={value}
              alt=""
              className="h-full w-full object-cover"
              onError={(event) => {
                event.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <span className="flex h-full items-center justify-center text-slate-300">
              <ImageIcon className="h-7 w-7" />
            </span>
          )}
        </div>
        <div className="flex flex-col items-start gap-1.5">
          <Button type="button" variant="secondary" size="sm" onClick={() => inputRef.current?.click()}>
            {value ? 'Replace' : 'Upload'}
          </Button>
          {value && (
            <button
              type="button"
              onClick={() => onChange('')}
              className={cn('text-xs font-medium text-red-600 transition hover:underline')}
            >
              Remove
            </button>
          )}
        </div>
        <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
      </div>
      {hint && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

export default ImageUploadField;
